import React from 'react';
import { formatCurrency } from '../../utils/formatting';
import ProductImage from './ProductImage';
import { PdfIcon } from './Icons';
import { SHOP_URL, canClickImage, RESTRICTED_VENDORS } from '../../utils/constants';
import { HiOutlineTrash } from 'react-icons/hi2';

const ATTRIBUTE_KEY_FILTER = (key) => key.toLowerCase() !== 'installation manual';
const formatAttrKey = (key) => key.replace('_LeadTime', 'Lead Time').replace(/_/g, ' ').trim();
const formatAttrValue = (value) => value.replace(/_/g, ' ').trim();

function LineItem({
  item,
  index,
  currency,
  expandThreshold = 3,
  isExpanded = false,
  onToggleExpand,
  variant = 'card',
  // Detail-only
  isRemoved = false,
  onRemove,
  canRemove = false,
  displayQuantity,
  onQuantityChange,
  canEditQuantity = false,
  canEditLineItems = false,
  isQuantityLoading = false,
  showManual = false,
  unfulfilledQuantity = null,
  isOrder = false,
}) {
  const filteredAttrs = item.customAttributes
    ? item.customAttributes.filter((attr) => ATTRIBUTE_KEY_FILTER(attr.key))
    : [];
  const hasMany = filteredAttrs.length > expandThreshold;
  const attrsToShow = hasMany && !isExpanded ? filteredAttrs.slice(0, expandThreshold) : filteredAttrs;
  const qty = displayQuantity !== undefined ? displayQuantity : item.quantity;

  const TitleContent = (
    <>
      {item.title}
      {item.variantTitle && !RESTRICTED_VENDORS.includes(item.vendor) && (
        <span className="variant-title">&nbsp;/&nbsp;{item.variantTitle}</span>
      )}
    </>
  );

  return (
    <div
      className={`line-item${isRemoved ? ' line-item--removed' : ''}`}
    >
      <div className="item-image">
        <ProductImage
          canClick={canClickImage(item)}
          handle={item.handle}
          src={item.image}
          alt={item.imageAltText}
        />
      </div>

      <div className="item-title">

        <div className="item-title-inner">
          {canClickImage(item) ? (
            <a
              className="item-title-link"
              href={`${SHOP_URL}/products/${item.handle}?variant=${item.variantId}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {TitleContent}
            </a>
          ) : (
            <span className="item-title-link">
              {TitleContent}
            </span>
          )}

          {variant === 'detail' && isOrder && unfulfilledQuantity === 0 && (
            // <div className="item-unfulfilled-quantity">
              <span className="pill-button pill-button-yellow pill-button-title">fulfilled</span>
            // </div>
          )}
        </div>

        
        

        

        {showManual && (item.manual || item.variantManual) && (
          <div className="item-manual">
            <a href={item.variantManual || item.manual} target="_blank" rel="noopener noreferrer">
              Installation Manual
              <PdfIcon />
            </a>
          </div>
        )}

        {item.sku && !RESTRICTED_VENDORS.includes(item.vendor) && (
          <div className="item-sku">SKU: {item.sku}</div>
        )}

        {filteredAttrs.length > 0 && (
          <>
            <div
              className={`item-custom-attributes expandable-content ${hasMany && !isExpanded ? 'item-custom-attributes--collapsed' : ''}`}
            >
              {attrsToShow.map((attr, attrIdx) => (
                <div key={attrIdx} className="custom-attribute">
                  <span className="attribute-key">{formatAttrKey(attr.key)}</span>:&nbsp;
                  <span className="attribute-value">{formatAttrValue(attr.value)}</span>
                </div>
              ))}
            </div>
            {hasMany && (
              <button
                className="btn-show-more-attributes"
                onClick={() => onToggleExpand?.(index)}
                type="button"
                aria-expanded={isExpanded}
              >
                <span className="expandable-content__toggle-icon" />
                <span className="expandable-content__toggle-text">
                  {isExpanded ? 'Show Less' : `Show ${filteredAttrs.length - expandThreshold} More`}
                </span>
              </button>
            )}
          </>
        )}
      </div>

      {variant === 'card' || isOrder && (
        <>
          <div className="item-quantity">Qty: {item.quantity}</div>
          <div className="item-price">
            {formatCurrency(item.price * item.quantity, currency)}
          </div>
        </>
      )}

      {variant === 'detail' && !isOrder && (
        <>
          {canEditQuantity && canEditLineItems ? (
            <div
              className={`quantity-selector quantity-selector__quote item-quantity${isQuantityLoading ? ' quantity-selector--loading' : ''}`}
              aria-busy={isQuantityLoading}
            >
              {isQuantityLoading && (
                <span className="quantity-selector__spinner" aria-hidden="true" />
              )}
              <button
                type="button"
                className="quantity-selector__button qty-minus"
                data-action="decrease-quantity"
                onClick={() => onQuantityChange?.(index, qty - 1)}
                disabled={isRemoved || !canEditQuantity || isQuantityLoading}
                aria-label="Decrease Quantity"
                title="Decrease Quantity"
              >
                -
              </button>
              <input
                id={`qty-${index}`}
                type="number"
                min="1"
                value={qty}
                onChange={(e) => onQuantityChange?.(index, parseInt(e.target.value, 10))}
                disabled={isRemoved || !canEditQuantity || isQuantityLoading}
                className="quantity-input quantity-selector__value"
              />
              <button
                type="button"
                className="quantity-selector__button qty-plus"
                data-action="increase-quantity"
                onClick={() => onQuantityChange?.(index, qty + 1)}
                disabled={isRemoved || !canEditQuantity || isQuantityLoading}
                aria-label="Increase Quantity"
                title="Increase Quantity"
              >
                +
              </button>
            </div>
          ) : (
            null
          )}

          <div className="item-price-container">
            <div className="item-price">
              {formatCurrency(item.price * qty, currency)}
            </div>
            {canRemove && (
              <button
                className={`btn-remove-item ${isRemoved ? 'btn-remove-item--undo' : ''}`}
                onClick={() => onRemove?.(index)}
                title={isRemoved ? 'Undo remove' : 'Remove item'}
              >
                {isRemoved ? 'Undo' : <HiOutlineTrash />}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default LineItem;
